import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';
import {
  RefreshCw, AlertCircle, ArrowLeft, ChevronDown, ChevronUp,
  Calendar, Users, Clock, Activity, BarChart3, DollarSign
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

// TypeScript Interfaces
interface DailyUsageRecord {
  id: number;
  user_identifier: string;
  usage_date: string;
  total_minutes: number;
  request_count: number;
  user_tier: string;
  created_at: Date;
  updated_at: Date;
}

interface AggregatedUserRecord {
  user_identifier: string;
  user_tier: string;
  total_minutes: number;
  request_count: number;
  total_cost: number;
  date_range_start: string;
  date_range_end: string;
  days_active: number;
  daily_records: DailyUsageRecord[];
}

interface TierBreakdown {
  tier: string;
  total_minutes: number;
  total_requests: number;
  user_count: number;
  total_cost?: number;
}

interface TopUser {
  user_identifier: string;
  total_minutes: number;
  request_count: number;
  user_tier: string;
}

interface DailyTrend {
  usage_date: string;
  total_minutes: number;
  total_requests: number;
  unique_users: number;
  total_cost?: number;
}

interface InputTypeBreakdown {
  input_type: string;
  count: number;
  total_cost: number;
}

interface LanguageBreakdown {
  language: string;
  count: number;
  total_cost: number;
}

interface UserLocale {
  user_identifier: string;
  country: string | null;
  language: string | null;
}

interface DailyUsageStatistics {
  totalMinutes: number;
  totalRequests: number;
  averageMinutesPerUser: number;
  averageRequestsPerUser: number;
  uniqueUsers: number;
  usageByTier: TierBreakdown[];
  topUsersByMinutes: TopUser[];
  topUsersByRequests: TopUser[];
  dailyTrend: DailyTrend[];
  totalCost?: number;
  averageCostPerUser?: number;
  costByTier?: { tier: string; cost: number; percentage: number }[];
  inputTypeBreakdown?: InputTypeBreakdown[];
  languageBreakdown?: LanguageBreakdown[];
  userLocales?: UserLocale[];
}

interface DailyUsagePaginatedResponse {
  records: DailyUsageRecord[];
  statistics: DailyUsageStatistics;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

type SortableColumn = 'usage_date' | 'total_minutes' | 'request_count' | 'user_identifier' | 'user_tier' | 'days_active' | 'total_cost';

const TIER_COLORS: { [key: string]: string } = {
  'free': '#60A5FA',
  'premium_monthly_1': '#10B981',
  'premium_monthly_2': '#F59E0B'
};

// Binary free/premium colors for the Top Users charts, matching the Both/Premium/Free toggle
const TIER_GROUP_COLORS = { free: '#60A5FA', premium: '#10B981' } as const;
const getTierGroupColor = (tier: string) => tier.startsWith('premium') ? TIER_GROUP_COLORS.premium : TIER_GROUP_COLORS.free;

const INPUT_TYPE_COLORS: Record<string, string> = {
  microphone:  '#A855F7',
  youtube:     '#EF4444',
  file_upload: '#F97316',
  app_share:   '#06B6D4',
  unknown:     '#6B7280',
};

const getInputTypeBadgeClass = (type: string) => {
  switch (type) {
    case 'microphone':  return 'bg-purple-500/20 text-purple-400';
    case 'youtube':     return 'bg-red-500/20 text-red-400';
    case 'file_upload': return 'bg-orange-500/20 text-orange-400';
    case 'app_share':   return 'bg-cyan-500/20 text-cyan-400';
    default:            return 'bg-gray-500/20 text-gray-400';
  }
};

// Palette cycled over detected languages (the set is open-ended, unlike input types)
const LANGUAGE_COLORS = [
  '#3B82F6', '#A855F7', '#22C55E', '#F97316', '#EF4444',
  '#06B6D4', '#EAB308', '#EC4899', '#8B5CF6', '#14B8A6',
];

// Transcription services report language inconsistently: Whisper returns full
// lowercase names ("english"), AssemblyAI returns ISO codes ("en")
const languageDisplayNames = new Intl.DisplayNames(['en'], { type: 'language' });
const formatLanguageName = (lang: string): string => {
  if (/^[a-z]{2,3}(-[A-Za-z]{2,4})?$/i.test(lang)) {
    try {
      const name = languageDisplayNames.of(lang);
      if (name && name !== lang) return name;
    } catch { /* fall through to capitalization */ }
  }
  return lang.charAt(0).toUpperCase() + lang.slice(1);
};

// "SE" -> 🇸🇪 via Unicode regional indicator symbols
const countryCodeToFlag = (code: string): string => {
  if (!/^[A-Za-z]{2}$/.test(code)) return '';
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

const regionDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' });
const formatCountryName = (code: string): string => {
  try {
    return regionDisplayNames.of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
};

// Pricing constants for Whisper transcription
const WHISPER_RATES = {
  BASE: 0.04,      // $0.04/hour - whisper-v3-turbo (current for all tiers)
  PREMIUM: 0.36    // $0.36/hour - future premium rate
} as const;

/**
 * Calculate cost for audio transcription based on minutes and tier
 * Premium tiers (premium_monthly_1, premium_monthly_2) use premium rate
 * Free tier uses base rate
 */
const calculateWhisperCost = (minutes: number, tier: string = 'free'): number => {
  const rate = tier.startsWith('premium') ? WHISPER_RATES.PREMIUM : WHISPER_RATES.BASE;
  return (minutes / 60) * rate;
};

/**
 * Aggregate flat DailyUsageRecord[] by user_identifier.
 * Used when This Week, Last Week, This Month, or Last Month quick filter is active.
 * Returns one AggregatedUserRecord per unique user, sorted by total_minutes desc.
 */
const aggregateRecordsByUser = (records: DailyUsageRecord[]): AggregatedUserRecord[] => {
  const map = new Map<string, AggregatedUserRecord>();

  for (const record of records) {
    const existing = map.get(record.user_identifier);
    if (existing) {
      existing.total_minutes += record.total_minutes;
      existing.request_count += record.request_count;
      existing.total_cost += calculateWhisperCost(record.total_minutes, record.user_tier);
      existing.daily_records.push(record);
      if (record.usage_date < existing.date_range_start) {
        existing.date_range_start = record.usage_date;
      }
      if (record.usage_date > existing.date_range_end) {
        existing.date_range_end = record.usage_date;
      }
    } else {
      map.set(record.user_identifier, {
        user_identifier: record.user_identifier,
        user_tier: record.user_tier,
        total_minutes: record.total_minutes,
        request_count: record.request_count,
        total_cost: calculateWhisperCost(record.total_minutes, record.user_tier),
        date_range_start: record.usage_date,
        date_range_end: record.usage_date,
        days_active: 1,
        daily_records: [record]
      });
    }
  }

  const result: AggregatedUserRecord[] = Array.from(map.values());
  result.forEach((agg) => {
    agg.daily_records.sort((a, b) => a.usage_date.localeCompare(b.usage_date));
    agg.days_active = agg.daily_records.length;
  });

  result.sort((a, b) => b.total_minutes - a.total_minutes);
  return result;
};

/**
 * Format cost for display with $ symbol and 2 decimal places
 */
const formatCost = (cost: number): string => {
  return `$${cost.toFixed(2)}`;
};

/**
 * Enrich statistics with computed cost fields
 */
const enrichStatisticsWithCosts = (
  statistics: DailyUsageStatistics
): DailyUsageStatistics => {
  // Enrich tier breakdown with costs (must do this first to get tier-specific costs)
  const enrichedUsageByTier = statistics.usageByTier.map(tier => ({
    ...tier,
    total_cost: calculateWhisperCost(tier.total_minutes, tier.tier)
  }));

  // Calculate total cost from all tier usage (using enriched data)
  const totalCost = enrichedUsageByTier.reduce(
    (sum, tier) => sum + (tier.total_cost || 0),
    0
  );

  // Calculate average cost per user
  const averageCostPerUser = statistics.uniqueUsers > 0
    ? totalCost / statistics.uniqueUsers
    : 0;

  // Create cost breakdown for pie chart
  const costByTier = enrichedUsageByTier.map(tier => ({
    tier: tier.tier,
    cost: tier.total_cost!,
    percentage: totalCost > 0 ? (tier.total_cost! / totalCost) * 100 : 0
  }));

  // Enrich daily trend with costs using weighted average based on tier distribution
  const enrichedDailyTrend = statistics.dailyTrend.map(day => {
    // Calculate weighted cost for this day based on tier distribution
    let dayCost = 0;
    statistics.usageByTier.forEach(tier => {
      const tierRate = tier.tier.startsWith('premium') ? WHISPER_RATES.PREMIUM : WHISPER_RATES.BASE;
      const tierProportion = statistics.totalMinutes > 0 ? tier.total_minutes / statistics.totalMinutes : 0;
      dayCost += (day.total_minutes * tierProportion / 60) * tierRate;
    });
    return {
      ...day,
      total_cost: dayCost
    };
  });

  return {
    ...statistics,
    totalCost,
    averageCostPerUser,
    usageByTier: enrichedUsageByTier,
    costByTier,
    dailyTrend: enrichedDailyTrend
  };
};

/**
 * Format Date object to YYYY-MM-DD string (local timezone)
 */
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 */
const getTodayDateString = (): string => {
  const today = new Date();
  return formatDateForInput(today);
};

/**
 * Get yesterday's date in YYYY-MM-DD format (local timezone)
 */
const getYesterdayDateString = (): string => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return formatDateForInput(yesterday);
};

/**
 * Get Monday of current week in YYYY-MM-DD format (ISO 8601 - week starts Monday)
 */
const getMondayOfCurrentWeek = (): string => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysFromMonday);
  return formatDateForInput(monday);
};

/**
 * Get first day of current month in YYYY-MM-DD format (local timezone)
 */
const getFirstDayOfCurrentMonth = (): string => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  return formatDateForInput(firstDay);
};

/**
 * Get Monday of last week in YYYY-MM-DD format (ISO 8601)
 */
const getMondayOfLastWeek = (): string => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const lastMonday = new Date(today);
  lastMonday.setDate(today.getDate() - daysFromMonday - 7); // Go back one week from this Monday
  return formatDateForInput(lastMonday);
};

/**
 * Get Sunday of last week in YYYY-MM-DD format (ISO 8601)
 */
const getSundayOfLastWeek = (): string => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const lastSunday = new Date(today);
  lastSunday.setDate(today.getDate() - daysFromMonday - 1); // Last Sunday
  return formatDateForInput(lastSunday);
};

/**
 * Get first day of last month in YYYY-MM-DD format (local timezone)
 */
const getFirstDayOfLastMonth = (): string => {
  const today = new Date();
  const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return formatDateForInput(firstDayLastMonth);
};

/**
 * Get last day of last month in YYYY-MM-DD format (local timezone)
 */
const getLastDayOfLastMonth = (): string => {
  const today = new Date();
  const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0); // Day 0 = last day of previous month
  return formatDateForInput(lastDayLastMonth);
};

/**
 * Get initial filter values from URL params, defaulting to "Today" if no dates present
 */
const getInitialFilters = (searchParams: URLSearchParams) => {
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';
  const userTier = searchParams.get('userTier') || '';
  const userIdentifier = searchParams.get('userIdentifier') || '';

  // If no dates in URL, default to "Today"
  if (!startDate && !endDate) {
    const todayDate = getTodayDateString();
    return {
      filters: {
        startDate: todayDate,
        endDate: todayDate,
        userTier,
        userIdentifier
      },
      quickFilter: 'today' as const
    };
  }

  // Use URL params
  const quickFilter = searchParams.get('quickFilter') as 'today' | 'yesterday' | 'week' | 'month' | 'lastWeek' | 'lastMonth' | null;
  return {
    filters: { startDate, endDate, userTier, userIdentifier },
    quickFilter
  };
};

const DailyUsage: React.FC = () => {
  // Data state
  const [data, setData] = useState<DailyUsagePaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination & URL params
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [limit] = useState(50);

  // Filtering state
  const [filterInputs, setFilterInputs] = useState(() => {
    const initial = getInitialFilters(searchParams);
    return initial.filters;
  });
  const [activeFilters, setActiveFilters] = useState(() => {
    const initial = getInitialFilters(searchParams);
    return initial.filters;
  });
  const [activeQuickFilter, setActiveQuickFilter] = useState<'today' | 'yesterday' | 'week' | 'month' | 'lastWeek' | 'lastMonth' | null>(() => {
    const initial = getInitialFilters(searchParams);
    return initial.quickFilter;
  });

  // Sorting state
  const [sortBy, setSortBy] = useState<SortableColumn>(
    (searchParams.get('sortBy') as SortableColumn) || 'usage_date'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc'
  );

  // UI state
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(
    new Set(['tier-breakdown', 'cost-breakdown', 'input-type-breakdown', 'top-users-minutes', 'top-users-requests', 'daily-trend', 'records-table', 'language-breakdown', 'country-breakdown'])
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Drill-down for the Top 10 Users charts: filter to premium or free tier, or show both
  const [topUsersTierFilter, setTopUsersTierFilter] = useState<'all' | 'premium' | 'free'>('all');

  // Tracks which aggregated user rows are expanded (shows per-day sub-rows)
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  // Controls whether the table shows aggregated (per-user) or detailed (per-record) view
  const [viewMode, setViewMode] = useState<'aggregated' | 'detailed'>('detailed');

  const isAggregatedView = viewMode === 'aggregated';

  // Detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const effectiveLimit = isAggregatedView ? 1000 : limit;
        const params = new URLSearchParams({
          page: isAggregatedView ? '1' : page.toString(),
          limit: effectiveLimit.toString(),
          sortBy: (sortBy === 'days_active' || sortBy === 'total_cost') ? 'total_minutes' : sortBy,
          sortOrder,
          ...(activeFilters.startDate && { startDate: activeFilters.startDate }),
          ...(activeFilters.endDate && { endDate: activeFilters.endDate }),
          ...(activeFilters.userTier && { userTier: activeFilters.userTier }),
          ...(activeFilters.userIdentifier && { userIdentifier: activeFilters.userIdentifier })
        });

        const response = await fetch(`https://api.notely.one/dashboard/daily-usage?${params}`);
        if (!response.ok) throw new Error('Failed to fetch daily usage data');
        const jsonData = await response.json();
        const enrichedData = {
          ...jsonData,
          statistics: enrichStatisticsWithCosts(jsonData.statistics)
        };
        setData(enrichedData);
        setError(null);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, limit, sortBy, sortOrder, activeFilters, isAggregatedView]);

  // Update URL when filters/pagination change
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', page.toString());

    if (activeFilters.startDate) params.set('startDate', activeFilters.startDate);
    if (activeFilters.endDate) params.set('endDate', activeFilters.endDate);
    if (activeFilters.userTier) params.set('userTier', activeFilters.userTier);
    if (activeFilters.userIdentifier) params.set('userIdentifier', activeFilters.userIdentifier);
    if (activeQuickFilter) params.set('quickFilter', activeQuickFilter);
    if (sortBy !== 'usage_date') params.set('sortBy', sortBy);
    if (sortOrder !== 'desc') params.set('sortOrder', sortOrder);

    setSearchParams(params);
  }, [page, activeFilters, activeQuickFilter, sortBy, sortOrder, setSearchParams]);

  // Event handlers
  const handleInputChange = (key: keyof typeof filterInputs, value: string) => {
    setFilterInputs(prev => ({ ...prev, [key]: value }));

    // If user manually changes date inputs, deselect quick filter buttons
    if (key === 'startDate' || key === 'endDate') {
      setActiveQuickFilter(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setActiveFilters(filterInputs);
      setPage(1);
    }
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveFilters(filterInputs);
    setPage(1);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      startDate: '',
      endDate: '',
      userTier: '',
      userIdentifier: ''
    };
    setFilterInputs(emptyFilters);
    setActiveFilters(emptyFilters);
    setActiveQuickFilter(null);
    setViewMode('detailed');
    setPage(1);
  };

  const handleQuickFilter = (filterType: 'today' | 'yesterday' | 'week' | 'month' | 'lastWeek' | 'lastMonth') => {
    // If clicking already-active button, deselect it
    if (activeQuickFilter === filterType) {
      // Clear dates, show all data
      const newFilters = {
        ...filterInputs,
        startDate: '',
        endDate: ''
      };
      setFilterInputs(newFilters);
      setActiveFilters(newFilters);
      setActiveQuickFilter(null);
    } else {
      // Calculate date range based on filterType
      let startDate: string;
      let endDate: string;

      switch (filterType) {
        case 'today':
          startDate = getTodayDateString();
          endDate = getTodayDateString();
          break;
        case 'yesterday':
          startDate = getYesterdayDateString();
          endDate = getYesterdayDateString();
          break;
        case 'week':
          startDate = getMondayOfCurrentWeek();
          endDate = getTodayDateString();
          break;
        case 'month':
          startDate = getFirstDayOfCurrentMonth();
          endDate = getTodayDateString();
          break;
        case 'lastWeek':
          startDate = getMondayOfLastWeek();
          endDate = getSundayOfLastWeek();
          break;
        case 'lastMonth':
          startDate = getFirstDayOfLastMonth();
          endDate = getLastDayOfLastMonth();
          break;
      }

      // Apply immediately
      const newFilters = {
        ...filterInputs,
        startDate,
        endDate
      };
      setFilterInputs(newFilters);
      setActiveFilters(newFilters);
      setActiveQuickFilter(filterType);
      // Auto-switch to aggregated view for multi-day filters, detailed for single-day
      if (filterType === 'week' || filterType === 'month' || filterType === 'lastWeek' || filterType === 'lastMonth') {
        setViewMode('aggregated');
      } else {
        setViewMode('detailed');
      }
    }
    setExpandedUsers(new Set()); // Clear user expansions when filter changes
    setPage(1); // Reset pagination
  };

  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleExpandedUser = (userId: string) => {
    const next = new Set(expandedUsers);
    if (next.has(userId)) {
      next.delete(userId);
    } else {
      next.add(userId);
    }
    setExpandedUsers(next);
  };

  const goToProcessLogs = (userIdentifier: string) => {
    if (!userIdentifier) return;
    navigate(`/dashboard-process-logs?deviceId=${encodeURIComponent(userIdentifier)}&page=1`);
  };

  // Most frequent country + detected language per user, from statistics.userLocales
  const userLocaleMap = new Map<string, UserLocale>(
    (data?.statistics.userLocales ?? []).map((locale) => [locale.user_identifier, locale])
  );

  // MySQL's JSON_UNQUOTE turns JSON null into the literal string "null"
  const cleanLocaleValue = (value: string | null | undefined): string | null =>
    value && value !== 'null' ? value : null;

  // Unique users per country, from each user's dominant country
  const countryBreakdown = Array.from(
    (data?.statistics.userLocales ?? []).reduce((map, locale) => {
      const country = cleanLocaleValue(locale.country);
      if (country) map.set(country, (map.get(country) || 0) + 1);
      return map;
    }, new Map<string, number>())
  )
    .map(([country, count]) => ({ country, count }))
    .sort((a, b) => b.count - a.count);

  const renderUserLocale = (userIdentifier: string) => {
    const locale = userLocaleMap.get(userIdentifier);
    const country = cleanLocaleValue(locale?.country);
    const language = cleanLocaleValue(locale?.language);
    if (!country && !language) return null;
    return (
      <span className="ml-2 text-xs text-gray-400 whitespace-nowrap">
        {country && (
          <span title={country}>{countryCodeToFlag(country)} {country}</span>
        )}
        {country && language && ' · '}
        {language && formatLanguageName(language)}
      </span>
    );
  };

  const toggleCard = (id: string) => {
    const newExpandedCards = new Set(expandedCards);
    if (expandedCards.has(id)) {
      newExpandedCards.delete(id);
    } else {
      newExpandedCards.add(id);
    }
    setExpandedCards(newExpandedCards);
  };

  const getTierBadgeClass = (tier: string) => {
    switch (tier) {
      case 'free':
        return 'bg-gray-500/20 text-gray-400';
      case 'premium_monthly_1':
        return 'bg-green-500/20 text-green-400';
      case 'premium_monthly_2':
        return 'bg-amber-500/20 text-amber-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const formatTierName = (tier: string) => {
    return tier.replace(/_/g, ' ').toUpperCase();
  };

  // Filters a Top Users list down to the selected tier bucket (premium = any premium_* tier) and caps at 10
  const filterTopUsersByTier = (users: TopUser[], filter: 'all' | 'premium' | 'free'): TopUser[] => {
    const scoped = filter === 'all'
      ? users
      : users.filter((u) => filter === 'premium' ? u.user_tier.startsWith('premium') : u.user_tier === 'free');
    return scoped.slice(0, 10);
  };

  const TierFilterToggle = ({
    value,
    onChange,
  }: {
    value: 'all' | 'premium' | 'free';
    onChange: (value: 'all' | 'premium' | 'free') => void;
  }) => (
    <div
      className="inline-flex rounded-lg bg-gray-900/60 p-1 gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {(['all', 'premium', 'free'] as const).map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
            value === option
              ? 'bg-blue-500 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
        >
          {option === 'all' ? 'Both' : option === 'premium' ? 'Premium' : 'Free'}
        </button>
      ))}
    </div>
  );

  // Gray pulsing placeholder shown in place of a value while a background refetch is in flight
  const Skeleton = ({ className = '' }: { className?: string }) => (
    <div className={`animate-pulse bg-gray-600/50 rounded ${className}`} />
  );

  const SortIcon = ({ column }: { column: SortableColumn }) => {
    if (sortBy !== column) {
      return <ChevronDown className="w-4 h-4 opacity-30" />;
    }
    return sortOrder === 'asc'
      ? <ChevronUp className="w-4 h-4" />
      : <ChevronDown className="w-4 h-4" />;
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="p-4">
        <div className="p-4 border border-red-500 bg-red-100 text-red-700 rounded-lg">
          <AlertCircle className="h-4 w-4 inline mr-2" />
          Error: {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Compute aggregated records (only used when isAggregatedView is true)
  const aggregatedRecords: AggregatedUserRecord[] = isAggregatedView
    ? aggregateRecordsByUser(data.records).sort((a, b) => {
        let valA: string | number;
        let valB: string | number;
        switch (sortBy) {
          case 'usage_date':      valA = a.date_range_end;   valB = b.date_range_end;   break;
          case 'user_identifier': valA = a.user_identifier;  valB = b.user_identifier;  break;
          case 'user_tier':       valA = a.user_tier;        valB = b.user_tier;        break;
          case 'request_count':   valA = a.request_count;    valB = b.request_count;    break;
          case 'days_active':     valA = a.days_active;      valB = b.days_active;      break;
          case 'total_cost':      valA = a.total_cost;       valB = b.total_cost;       break;
          default:                valA = a.total_minutes;    valB = b.total_minutes;
        }
        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      })
    : [];

  return (
    <div className="p-4 space-y-4">
      {/* Navigation Section */}
      <div className="flex gap-2 flex-wrap">
        <a
          href="/dashboard-process-logs"
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Process Logs</span>
        </a>
      </div>

      {/* Main Card */}
      <Card className="bg-gray-800">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-white">Daily Usage Dashboard</CardTitle>
            <button
              onClick={() => window.location.reload()}
              className="text-blue-400 hover:text-blue-300"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {loading && (
            <div className="h-0.5 w-full bg-gray-700 rounded-full overflow-hidden mt-3">
              <div className="h-full w-1/3 bg-blue-500 rounded-full animate-[loading-sweep_1s_ease-in-out_infinite]" />
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setIsFiltersVisible(!isFiltersVisible)}
            className="md:hidden flex items-center gap-2 text-white bg-gray-700 px-3 py-1 rounded-lg mb-4"
          >
            {isFiltersVisible ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Filters
          </button>

          {/* Filter Form */}
          <form
            onSubmit={handleFilterSubmit}
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ${!isFiltersVisible ? 'hidden md:grid' : ''
              }`}
          >
            {/* Quick Date Filter Buttons */}
            <div className="lg:col-span-4 mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Quick Filters
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleQuickFilter('today')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    activeQuickFilter === 'today'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickFilter('yesterday')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    activeQuickFilter === 'yesterday'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickFilter('week')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    activeQuickFilter === 'week'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                >
                  This Week
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickFilter('lastWeek')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    activeQuickFilter === 'lastWeek'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                >
                  Last Week
                </button>

                <button
                  type="button"
                  onClick={() => handleQuickFilter('month')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    activeQuickFilter === 'month'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                >
                  This Month
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickFilter('lastMonth')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    activeQuickFilter === 'lastMonth'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                >
                  Last Month
                </button>
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                value={filterInputs.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                value={filterInputs.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            {/* User Tier */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                User Tier
              </label>
              <select
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                value={filterInputs.userTier}
                onChange={(e) => handleInputChange('userTier', e.target.value)}
                onKeyDown={handleKeyDown}
              >
                <option value="">All Tiers</option>
                <option value="free">Free</option>
                <option value="premium_monthly_1">Premium Monthly 1</option>
                <option value="premium_monthly_2">Premium Monthly 2</option>
              </select>
            </div>

            {/* User Identifier Search */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                User Identifier
              </label>
              <input
                type="text"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                placeholder="Search by user ID..."
                value={filterInputs.userIdentifier}
                onChange={(e) => handleInputChange('userIdentifier', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            {/* Action Buttons */}
            <div className="lg:col-span-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Reset Filters
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </form>

          {/* Statistics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {/* Total Minutes */}
            <Card className="bg-gray-800 text-white">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-gray-400">Total Minutes</p>
                    {loading ? (
                      <Skeleton className="h-6 lg:h-8 w-16 mt-1" />
                    ) : (
                      <p className="text-lg lg:text-2xl font-bold text-blue-400">
                        {data.statistics.totalMinutes.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <Clock className="w-6 h-6 lg:w-8 lg:h-8 text-gray-500" />
                </div>
              </CardContent>
            </Card>

            {/* Total Requests */}
            <Card className="bg-gray-800 text-white">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-gray-400">Total Requests</p>
                    {loading ? (
                      <Skeleton className="h-6 lg:h-8 w-16 mt-1" />
                    ) : (
                      <p className="text-lg lg:text-2xl font-bold text-green-400">
                        {data.statistics.totalRequests.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <Activity className="w-6 h-6 lg:w-8 lg:h-8 text-gray-500" />
                </div>
              </CardContent>
            </Card>

            {/* Unique Users */}
            <Card className="bg-gray-800 text-white">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-gray-400">Unique Users</p>
                    {loading ? (
                      <Skeleton className="h-6 lg:h-8 w-16 mt-1" />
                    ) : (
                      <p className="text-lg lg:text-2xl font-bold text-purple-400">
                        {data.statistics.uniqueUsers.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <Users className="w-6 h-6 lg:w-8 lg:h-8 text-gray-500" />
                </div>
              </CardContent>
            </Card>

            {/* Avg Minutes/User */}
            <Card className="bg-gray-800 text-white">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-gray-400">Avg Min/User</p>
                    {loading ? (
                      <Skeleton className="h-6 lg:h-8 w-16 mt-1" />
                    ) : (
                      <p className="text-lg lg:text-2xl font-bold text-orange-400">
                        {data.statistics.averageMinutesPerUser.toFixed(1)}
                      </p>
                    )}
                  </div>
                  <BarChart3 className="w-6 h-6 lg:w-8 lg:h-8 text-gray-500" />
                </div>
              </CardContent>
            </Card>

            {/* Total Cost */}
            <Card className="bg-gray-800 text-white">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-gray-400">Total Cost</p>
                    {loading ? (
                      <Skeleton className="h-6 lg:h-8 w-16 mt-1" />
                    ) : (
                      <p className="text-lg lg:text-2xl font-bold text-emerald-400">
                        {formatCost(data.statistics.totalCost || 0)}
                      </p>
                    )}
                  </div>
                  <DollarSign className="w-6 h-6 lg:w-8 lg:h-8 text-gray-500" />
                </div>
              </CardContent>
            </Card>

            {/* Avg Cost/User */}
            <Card className="bg-gray-800 text-white">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-gray-400">Avg Cost/User</p>
                    {loading ? (
                      <Skeleton className="h-6 lg:h-8 w-16 mt-1" />
                    ) : (
                      <p className="text-lg lg:text-2xl font-bold text-teal-400">
                        {formatCost(data.statistics.averageCostPerUser || 0)}
                      </p>
                    )}
                  </div>
                  <DollarSign className="w-6 h-6 lg:w-8 lg:h-8 text-gray-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart 1: Usage by User Tier */}
          <Card className="bg-gray-800 text-white mb-4">
            <CardHeader
              className="p-4 lg:p-6 cursor-pointer"
              onClick={() => toggleCard('tier-breakdown')}
            >
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Usage by User Tier</CardTitle>
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${expandedCards.has('tier-breakdown') ? 'rotate-180' : ''
                    }`}
                />
              </div>
            </CardHeader>
            {expandedCards.has('tier-breakdown') && (
              <CardContent className="p-4 lg:p-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-gray-300">
                    <thead className="bg-gray-700/50 text-gray-400 text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2">Tier</th>
                        <th className="px-3 py-2 text-right">Users</th>
                        <th className="px-3 py-2 text-right">Minutes</th>
                        <th className="px-3 py-2 text-right">Requests</th>
                        <th className="px-3 py-2 text-right">% of Minutes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {data.statistics.usageByTier.map((tier) => (
                        <tr key={tier.tier}>
                          <td className="px-3 py-2.5">
                            <span className="flex items-center gap-2">
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: TIER_COLORS[tier.tier] || '#9CA3AF' }}
                              />
                              {formatTierName(tier.tier)}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right">{tier.user_count.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right text-blue-400 font-medium">{tier.total_minutes.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right text-green-400 font-medium">{tier.total_requests.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right">
                            {data.statistics.totalMinutes > 0
                              ? ((tier.total_minutes / data.statistics.totalMinutes) * 100).toFixed(1)
                              : '0.0'}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Chart 2: Cost Distribution by Tier */}
          <Card className="bg-gray-800 text-white mb-4">
            <CardHeader
              className="p-4 lg:p-6 cursor-pointer"
              onClick={() => toggleCard('cost-breakdown')}
            >
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Cost Distribution by Tier</CardTitle>
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${
                    expandedCards.has('cost-breakdown') ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </CardHeader>
            {expandedCards.has('cost-breakdown') && (
              <CardContent className="p-4 lg:p-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-gray-300">
                    <thead className="bg-gray-700/50 text-gray-400 text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2">Tier</th>
                        <th className="px-3 py-2 text-right">Cost</th>
                        <th className="px-3 py-2 text-right">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {data.statistics.costByTier?.map((tier) => (
                        <tr key={tier.tier}>
                          <td className="px-3 py-2.5">
                            <span className="flex items-center gap-2">
                              <span
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: TIER_COLORS[tier.tier] || '#9CA3AF' }}
                              />
                              {formatTierName(tier.tier)}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right text-amber-400 font-medium">{formatCost(tier.cost)}</td>
                          <td className="px-3 py-2.5 text-right">{tier.percentage.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Chart 3: Requests by Input Type */}
          {data.statistics.inputTypeBreakdown && data.statistics.inputTypeBreakdown.length > 0 && (
            <Card className="bg-gray-800 text-white mb-4">
              <CardHeader
                className="p-4 lg:p-6 cursor-pointer"
                onClick={() => toggleCard('input-type-breakdown')}
              >
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Requests by Input Type</CardTitle>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      expandedCards.has('input-type-breakdown') ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </CardHeader>
              {expandedCards.has('input-type-breakdown') && (
                <CardContent className="p-4 lg:p-6">
                  <div className="h-80 lg:h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.statistics.inputTypeBreakdown}
                          dataKey="count"
                          nameKey="input_type"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ input_type, percent }: { input_type: string; percent: number }) =>
                            `${input_type} ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {data.statistics.inputTypeBreakdown.map((entry) => (
                            <Cell
                              key={entry.input_type}
                              fill={INPUT_TYPE_COLORS[entry.input_type] ?? INPUT_TYPE_COLORS.unknown}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: 'none', color: '#fff' }}
                          formatter={(value: number, name: string) => [`${value} requests`, name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 justify-center">
                    {data.statistics.inputTypeBreakdown.map((entry) => (
                      <span
                        key={entry.input_type}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getInputTypeBadgeClass(entry.input_type)}`}
                      >
                        {entry.input_type}: {entry.count}
                      </span>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Languages — detected transcription language breakdown */}
          {data.statistics.languageBreakdown && data.statistics.languageBreakdown.length > 0 && (
            <Card className="bg-gray-800 text-white mb-4">
              <CardHeader
                className="p-4 lg:p-6 cursor-pointer"
                onClick={() => toggleCard('language-breakdown')}
              >
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Languages</CardTitle>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      expandedCards.has('language-breakdown') ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </CardHeader>
              {expandedCards.has('language-breakdown') && (
                <CardContent className="p-4 lg:p-6">
                  <div className="h-80 lg:h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.statistics.languageBreakdown}
                          dataKey="count"
                          nameKey="language"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ language, percent }: { language: string; percent: number }) =>
                            `${formatLanguageName(language)} ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {data.statistics.languageBreakdown.map((entry, index) => (
                            <Cell
                              key={entry.language}
                              fill={LANGUAGE_COLORS[index % LANGUAGE_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1F2937', border: 'none', color: '#fff' }}
                          formatter={(value: number, name: string) => [`${value} requests`, formatLanguageName(name)]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 justify-center">
                    {data.statistics.languageBreakdown.map((entry, index) => (
                      <span
                        key={entry.language}
                        className="px-2 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${LANGUAGE_COLORS[index % LANGUAGE_COLORS.length]}33`,
                          color: LANGUAGE_COLORS[index % LANGUAGE_COLORS.length]
                        }}
                      >
                        {formatLanguageName(entry.language)}: {entry.count.toLocaleString()}
                      </span>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Countries — unique users by dominant country */}
          {countryBreakdown.length > 0 && (
            <Card className="bg-gray-800 text-white mb-4">
              <CardHeader
                className="p-4 lg:p-6 cursor-pointer"
                onClick={() => toggleCard('country-breakdown')}
              >
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Users by Country</CardTitle>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      expandedCards.has('country-breakdown') ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </CardHeader>
              {expandedCards.has('country-breakdown') && (
                <CardContent className="p-4 lg:p-6">
                  <div style={{ height: Math.max(200, countryBreakdown.length * 30 + 40) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={countryBreakdown}
                        layout="vertical"
                        margin={{ left: 16, right: 48, top: 4, bottom: 4 }}
                      >
                        <XAxis type="number" allowDecimals={false} stroke="#9CA3AF" tick={{ fontSize: 11 }} />
                        <YAxis
                          dataKey="country"
                          type="category"
                          width={150}
                          interval={0}
                          stroke="#9CA3AF"
                          tick={{ fontSize: 12, fill: '#D1D5DB' }}
                          tickFormatter={(code: string) => `${countryCodeToFlag(code)} ${formatCountryName(code)}`}
                        />
                        <Tooltip
                          cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                          contentStyle={{ backgroundColor: '#1F2937', border: 'none', color: '#fff' }}
                          formatter={(value: number) => [`${value} user${value === 1 ? '' : 's'}`, 'Users']}
                          labelFormatter={(code: string) => formatCountryName(code)}
                        />
                        <Bar dataKey="count" fill="#60A5FA" barSize={16} radius={[0, 4, 4, 0]}>
                          <LabelList dataKey="count" position="right" fill="#9CA3AF" fontSize={12} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Chart 4: Top Users by Minutes */}
          <Card className="bg-gray-800 text-white mb-4">
            <CardHeader
              className="p-4 lg:p-6 cursor-pointer"
              onClick={() => toggleCard('top-users-minutes')}
            >
              <div className="flex justify-between items-center gap-3">
                <CardTitle className="text-lg">Top 10 Users by Minutes</CardTitle>
                <div className="flex items-center gap-3">
                  <TierFilterToggle value={topUsersTierFilter} onChange={setTopUsersTierFilter} />
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${expandedCards.has('top-users-minutes') ? 'rotate-180' : ''
                      }`}
                  />
                </div>
              </div>
            </CardHeader>
            {expandedCards.has('top-users-minutes') && (() => {
              const minutesData = filterTopUsersByTier(data.statistics.topUsersByMinutes, topUsersTierFilter);
              return (
              <CardContent className="p-4 lg:p-6">
                {minutesData.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-12">No {topUsersTierFilter} users in this period.</p>
                ) : (
                <>
                {topUsersTierFilter === 'all' && (
                  <div className="flex items-center gap-4 mb-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TIER_GROUP_COLORS.free }} />
                      Free
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TIER_GROUP_COLORS.premium }} />
                      Premium
                    </span>
                  </div>
                )}
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={minutesData}
                      layout={isMobile ? "vertical" : "horizontal"}
                      margin={isMobile ? { left: 120 } : { bottom: 80 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      {isMobile ? (
                        <>
                          <YAxis
                            dataKey="user_identifier"
                            type="category"
                            stroke="#9CA3AF"
                            tick={{ fontSize: 10 }}
                            width={110}
                          />
                          <XAxis type="number" stroke="#9CA3AF" />
                        </>
                      ) : (
                        <>
                          <XAxis
                            dataKey="user_identifier"
                            stroke="#9CA3AF"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis stroke="#9CA3AF" />
                        </>
                      )}
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', color: '#fff' }}
                        formatter={(value: number, name: string, props: any) => [
                          `${value.toLocaleString()} minutes (${props.payload.request_count} requests, ${formatTierName(props.payload.user_tier)})`,
                          'Total Minutes'
                        ]}
                      />
                      <Bar dataKey="total_minutes" name="Minutes">
                        {minutesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getTierGroupColor(entry.user_tier)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                </>
                )}
              </CardContent>
              );
            })()}
          </Card>

          {/* Chart 5: Top Users by Requests */}
          <Card className="bg-gray-800 text-white mb-4">
            <CardHeader
              className="p-4 lg:p-6 cursor-pointer"
              onClick={() => toggleCard('top-users-requests')}
            >
              <div className="flex justify-between items-center gap-3">
                <CardTitle className="text-lg">Top 10 Users by Requests</CardTitle>
                <div className="flex items-center gap-3">
                  <TierFilterToggle value={topUsersTierFilter} onChange={setTopUsersTierFilter} />
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${expandedCards.has('top-users-requests') ? 'rotate-180' : ''
                      }`}
                  />
                </div>
              </div>
            </CardHeader>
            {expandedCards.has('top-users-requests') && (() => {
              const requestsData = filterTopUsersByTier(data.statistics.topUsersByRequests, topUsersTierFilter);
              return (
              <CardContent className="p-4 lg:p-6">
                {requestsData.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-12">No {topUsersTierFilter} users in this period.</p>
                ) : (
                <>
                {topUsersTierFilter === 'all' && (
                  <div className="flex items-center gap-4 mb-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TIER_GROUP_COLORS.free }} />
                      Free
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TIER_GROUP_COLORS.premium }} />
                      Premium
                    </span>
                  </div>
                )}
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={requestsData}
                      layout={isMobile ? "vertical" : "horizontal"}
                      margin={isMobile ? { left: 120 } : { bottom: 80 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      {isMobile ? (
                        <>
                          <YAxis
                            dataKey="user_identifier"
                            type="category"
                            stroke="#9CA3AF"
                            tick={{ fontSize: 10 }}
                            width={110}
                          />
                          <XAxis type="number" stroke="#9CA3AF" />
                        </>
                      ) : (
                        <>
                          <XAxis
                            dataKey="user_identifier"
                            stroke="#9CA3AF"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis stroke="#9CA3AF" />
                        </>
                      )}
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', color: '#fff' }}
                        formatter={(value: number, name: string, props: any) => [
                          `${value.toLocaleString()} requests (${props.payload.total_minutes.toLocaleString()} minutes, ${formatTierName(props.payload.user_tier)})`,
                          'Total Requests'
                        ]}
                      />
                      <Bar dataKey="request_count" name="Requests">
                        {requestsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getTierGroupColor(entry.user_tier)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                </>
                )}
              </CardContent>
              );
            })()}
          </Card>

          {/* Chart 6: Usage Trend */}
          <Card className="bg-gray-800 text-white mb-4">
            <CardHeader
              className="p-4 lg:p-6 cursor-pointer"
              onClick={() => toggleCard('daily-trend')}
            >
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Usage Trend (Last 30 Days)</CardTitle>
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${expandedCards.has('daily-trend') ? 'rotate-180' : ''
                    }`}
                />
              </div>
            </CardHeader>
            {expandedCards.has('daily-trend') && (
              <CardContent className="p-4 lg:p-6">
                <div className="h-80 lg:h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.statistics.dailyTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="usage_date"
                        stroke="#9CA3AF"
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        interval={isMobile ? 2 : 0}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        tickFormatter={(date) => {
                          const d = new Date(date);
                          return `${d.getMonth() + 1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis yAxisId="left" stroke="#9CA3AF" />
                      <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', color: '#fff' }}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="total_minutes"
                        stroke="#60A5FA"
                        name="Minutes"
                        strokeWidth={2}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="total_requests"
                        stroke="#10B981"
                        name="Requests"
                        strokeWidth={2}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="unique_users"
                        stroke="#F59E0B"
                        name="Unique Users"
                        strokeWidth={2}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="total_cost"
                        stroke="#10B981"
                        name="Cost ($)"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Records Table Card */}
          <Card className="bg-gray-800 text-white mb-4">
            <CardHeader className="p-4 lg:p-6">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleCard('records-table')}
              >
                <CardTitle className="text-lg">
                  {isAggregatedView
                    ? `Usage Records — ${aggregatedRecords.length} users`
                    : `Usage Records (${data.pagination.total.toLocaleString()})`
                  }
                </CardTitle>
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${
                    expandedCards.has('records-table') ? 'rotate-180' : ''
                  }`}
                />
              </div>
              {/* View mode toggle */}
              <div
                className="flex items-center gap-1 mt-3"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setViewMode('aggregated')}
                  className={`px-3 py-1 rounded-l-lg text-sm font-medium transition-colors ${
                    viewMode === 'aggregated'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                >
                  Aggregated
                </button>
                <button
                  onClick={() => setViewMode('detailed')}
                  className={`px-3 py-1 rounded-r-lg text-sm font-medium transition-colors ${
                    viewMode === 'detailed'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                  }`}
                >
                  Detailed
                </button>
              </div>
            </CardHeader>
            {expandedCards.has('records-table') && (
              <CardContent className="p-4 lg:p-6">
                {/* Records Table - Desktop */}
                <div className="overflow-x-auto mb-6">
                  {isAggregatedView ? (
                    /* Aggregated view: one row per user, expandable to per-day */
                    <table className="hidden md:table w-full text-left text-gray-300">
                      <thead className="bg-gray-700/50 text-gray-400 text-sm">
                        <tr>
                          <th className="px-4 py-3 w-8"></th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('usage_date')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              Date Range
                              <SortIcon column="usage_date" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('user_identifier')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              User Identifier
                              <SortIcon column="user_identifier" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('user_tier')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              User Tier
                              <SortIcon column="user_tier" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('days_active')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              Days Active
                              <SortIcon column="days_active" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('total_minutes')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              Total Minutes
                              <SortIcon column="total_minutes" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('request_count')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              Request Count
                              <SortIcon column="request_count" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('total_cost')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              Est. Cost
                              <SortIcon column="total_cost" />
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/50">
                        {aggregatedRecords.map((agg) => {
                          const isExpanded = expandedUsers.has(agg.user_identifier);
                          return (
                            <React.Fragment key={agg.user_identifier}>
                              {/* Aggregated summary row */}
                              <tr
                                className="hover:bg-gray-700/30 transition-colors cursor-pointer"
                                onClick={() => toggleExpandedUser(agg.user_identifier)}
                              >
                                <td className="px-4 py-3 text-gray-400">
                                  {isExpanded
                                    ? <ChevronUp className="w-4 h-4" />
                                    : <ChevronDown className="w-4 h-4" />
                                  }
                                </td>
                                <td className="p-4 text-sm text-gray-400 whitespace-nowrap">
                                  {new Date(agg.date_range_start).toLocaleDateString()} – {new Date(agg.date_range_end).toLocaleDateString()}
                                </td>
                                <td className="p-4">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); goToProcessLogs(agg.user_identifier); }}
                                    className="font-mono text-sm text-blue-400 hover:text-blue-300 hover:underline"
                                    title="View process logs for this user"
                                  >
                                    {agg.user_identifier}
                                  </button>
                                  {renderUserLocale(agg.user_identifier)}
                                </td>
                                <td className="p-4">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierBadgeClass(agg.user_tier)}`}>
                                    {formatTierName(agg.user_tier)}
                                  </span>
                                </td>
                                <td className="p-4">
                                  <span className="text-purple-400 font-medium">
                                    {agg.days_active}
                                  </span>
                                </td>
                                <td className="p-4">
                                  <span className="text-blue-400 font-medium">
                                    {agg.total_minutes.toLocaleString()}
                                  </span>
                                </td>
                                <td className="p-4">
                                  <span className="text-green-400 font-medium">
                                    {agg.request_count.toLocaleString()}
                                  </span>
                                </td>
                                <td className="p-4">
                                  <span className="text-emerald-400 font-medium">
                                    {formatCost(agg.total_cost)}
                                  </span>
                                </td>
                              </tr>
                              {/* Per-day sub-rows */}
                              {isExpanded && agg.daily_records.map((record) => (
                                <tr
                                  key={`${record.user_identifier}-${record.usage_date}`}
                                  className="bg-gray-900/40 border-l-2 border-blue-500/40"
                                >
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2 text-xs text-gray-400 pl-8">
                                    {new Date(record.usage_date).toLocaleDateString()}
                                  </td>
                                  <td className="px-4 py-2">
                                    <button
                                      onClick={() => goToProcessLogs(record.user_identifier)}
                                      className="text-xs text-gray-600 font-mono hover:text-blue-300 hover:underline"
                                      title="View process logs for this user"
                                    >
                                      {record.user_identifier}
                                    </button>
                                  </td>
                                  <td className="px-4 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium opacity-60 ${getTierBadgeClass(record.user_tier)}`}>
                                      {formatTierName(record.user_tier)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2 text-xs text-blue-300">
                                    {record.total_minutes.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-xs text-green-300">
                                    {record.request_count.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-xs text-emerald-300">
                                    {formatCost(calculateWhisperCost(record.total_minutes, record.user_tier))}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    /* Standard view: one row per record */
                    <table className="hidden md:table w-full text-left text-gray-300">
                      <thead className="bg-gray-700/50 text-gray-400 text-sm">
                        <tr>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('usage_date')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              Usage Date
                              <SortIcon column="usage_date" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('user_identifier')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              User Identifier
                              <SortIcon column="user_identifier" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('user_tier')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              User Tier
                              <SortIcon column="user_tier" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('total_minutes')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              Total Minutes
                              <SortIcon column="total_minutes" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <button
                              onClick={() => handleSort('request_count')}
                              className="flex items-center gap-1 hover:text-white"
                            >
                              Request Count
                              <SortIcon column="request_count" />
                            </button>
                          </th>
                          <th className="px-4 py-3">
                            <span className="flex items-center gap-1">
                              Est. Cost
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/50">
                        {data.records.map((record) => (
                          <tr
                            key={`${record.user_identifier}-${record.usage_date}`}
                            className="hover:bg-gray-700/30 transition-colors"
                          >
                            <td className="p-4 text-sm text-gray-400">
                              {new Date(record.usage_date).toLocaleDateString()}
                            </td>
                            <td className="p-4">
                              <button
                                onClick={() => goToProcessLogs(record.user_identifier)}
                                className="font-mono text-sm text-blue-400 hover:text-blue-300 hover:underline"
                                title="View process logs for this user"
                              >
                                {record.user_identifier}
                              </button>
                              {renderUserLocale(record.user_identifier)}
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierBadgeClass(record.user_tier)}`}>
                                {formatTierName(record.user_tier)}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className="text-blue-400 font-medium">
                                {record.total_minutes.toLocaleString()}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className="text-green-400 font-medium">
                                {record.request_count.toLocaleString()}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className="text-emerald-400 font-medium">
                                {formatCost(calculateWhisperCost(record.total_minutes, record.user_tier))}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Records Mobile Cards */}
                <div className="md:hidden space-y-4 mb-6">
                  {isAggregatedView ? (
                    /* Aggregated view: one card per user, expandable to daily breakdown */
                    aggregatedRecords.map((agg) => {
                      const isExpanded = expandedUsers.has(agg.user_identifier);
                      return (
                        <Card key={agg.user_identifier} className="bg-gray-700">
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              {/* Header: date range + tier + chevron */}
                              <div
                                className="flex items-center justify-between border-b border-gray-600 pb-2 cursor-pointer"
                                onClick={() => toggleExpandedUser(agg.user_identifier)}
                              >
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm font-medium text-white">
                                    {new Date(agg.date_range_start).toLocaleDateString()} – {new Date(agg.date_range_end).toLocaleDateString()}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierBadgeClass(agg.user_tier)}`}>
                                    {formatTierName(agg.user_tier)}
                                  </span>
                                  {isExpanded
                                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                                    : <ChevronDown className="w-4 h-4 text-gray-400" />
                                  }
                                </div>
                              </div>

                              {/* User Identifier */}
                              <div>
                                <span className="text-xs text-gray-400">User Identifier</span>
                                <button
                                  onClick={() => goToProcessLogs(agg.user_identifier)}
                                  className="block font-mono text-sm text-blue-400 hover:text-blue-300 hover:underline break-all text-left"
                                  title="View process logs for this user"
                                >
                                  {agg.user_identifier}
                                </button>
                                {renderUserLocale(agg.user_identifier)}
                              </div>

                              {/* Aggregated metrics grid */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-800 rounded p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Clock className="w-4 h-4 text-blue-400" />
                                    <span className="text-xs text-gray-400">Total Min</span>
                                  </div>
                                  <span className="text-lg font-bold text-blue-400">
                                    {agg.total_minutes.toLocaleString()}
                                  </span>
                                </div>
                                <div className="bg-gray-800 rounded p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Activity className="w-4 h-4 text-green-400" />
                                    <span className="text-xs text-gray-400">Requests</span>
                                  </div>
                                  <span className="text-lg font-bold text-green-400">
                                    {agg.request_count.toLocaleString()}
                                  </span>
                                </div>
                                <div className="bg-gray-800 rounded p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <DollarSign className="w-4 h-4 text-emerald-400" />
                                    <span className="text-xs text-gray-400">Cost</span>
                                  </div>
                                  <span className="text-lg font-bold text-emerald-400">
                                    {formatCost(agg.total_cost)}
                                  </span>
                                </div>
                                <div className="bg-gray-800 rounded p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Calendar className="w-4 h-4 text-purple-400" />
                                    <span className="text-xs text-gray-400">Days Active</span>
                                  </div>
                                  <span className="text-lg font-bold text-purple-400">
                                    {agg.days_active}
                                  </span>
                                </div>
                              </div>

                              {/* Per-day breakdown (visible when expanded) */}
                              {isExpanded && (
                                <div className="mt-2 space-y-2 border-t border-gray-600 pt-2">
                                  <span className="text-xs text-gray-400 uppercase tracking-wide">Daily Breakdown</span>
                                  {agg.daily_records.map((record) => (
                                    <div
                                      key={`${record.user_identifier}-${record.usage_date}`}
                                      className="bg-gray-800/60 rounded p-3 border-l-2 border-blue-500/40"
                                    >
                                      <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs text-gray-300 font-medium">
                                          {new Date(record.usage_date).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2 text-xs">
                                        <div>
                                          <span className="text-gray-500">Min</span>
                                          <div className="text-blue-300 font-medium">{record.total_minutes.toLocaleString()}</div>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Req</span>
                                          <div className="text-green-300 font-medium">{record.request_count.toLocaleString()}</div>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Cost</span>
                                          <div className="text-emerald-300 font-medium">
                                            {formatCost(calculateWhisperCost(record.total_minutes, record.user_tier))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    /* Standard view: one card per record */
                    data.records.map((record) => (
                      <Card key={`${record.user_identifier}-${record.usage_date}`} className="bg-gray-700">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Date Header */}
                            <div className="flex items-center justify-between border-b border-gray-600 pb-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-white">
                                  {new Date(record.usage_date).toLocaleDateString()}
                                </span>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierBadgeClass(record.user_tier)}`}>
                                {formatTierName(record.user_tier)}
                              </span>
                            </div>

                            {/* User Identifier */}
                            <div>
                              <span className="text-xs text-gray-400">User Identifier</span>
                              <button
                                onClick={() => goToProcessLogs(record.user_identifier)}
                                className="block font-mono text-sm text-blue-400 hover:text-blue-300 hover:underline break-all text-left"
                                title="View process logs for this user"
                              >
                                {record.user_identifier}
                              </button>
                              {renderUserLocale(record.user_identifier)}
                            </div>

                            {/* Metrics Grid */}
                            <div className="grid grid-cols-3 gap-3">
                              <div className="bg-gray-800 rounded p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Clock className="w-4 h-4 text-blue-400" />
                                  <span className="text-xs text-gray-400">Minutes</span>
                                </div>
                                <span className="text-lg font-bold text-blue-400">
                                  {record.total_minutes.toLocaleString()}
                                </span>
                              </div>
                              <div className="bg-gray-800 rounded p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Activity className="w-4 h-4 text-green-400" />
                                  <span className="text-xs text-gray-400">Requests</span>
                                </div>
                                <span className="text-lg font-bold text-green-400">
                                  {record.request_count.toLocaleString()}
                                </span>
                              </div>
                              <div className="bg-gray-800 rounded p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <DollarSign className="w-4 h-4 text-emerald-400" />
                                  <span className="text-xs text-gray-400">Cost</span>
                                </div>
                                <span className="text-lg font-bold text-emerald-400">
                                  {formatCost(calculateWhisperCost(record.total_minutes, record.user_tier))}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                {/* Pagination — hidden in aggregated view (all records fetched in one call) */}
                {!isAggregatedView && <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
                  {/* Page Info */}
                  <div className="text-gray-300 text-sm">
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, data.pagination.total)} of {data.pagination.total.toLocaleString()} records
                  </div>

                  {/* Pagination Buttons */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handlePageChange(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                    >
                      Previous
                    </button>

                    <span className="text-gray-300">
                      Page {page} of {data.pagination.totalPages}
                    </span>

                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= data.pagination.totalPages}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>}
              </CardContent>
            )}
          </Card>

        </CardContent>
      </Card>
    </div>
  );
};

export default DailyUsage;
